import { decodeHtmlEntitiesDeep } from '~/utils/html-entities';

/**
 * Enrich TipTap nodes with metadata from `dataContent`, including default values.
 *
 * New array format example:
 * [
 *   { key: 'role', kind: 'select', required: true, options: [{ value: 'physician' }], value: 'nurse' },
 *   { key: 'topic', kind: 'input', placeholder: 'Specific topic', required: true, value: 'Diabetes' },
 * ]
 *
 * Old format example:
 * {
 *   input: { topic: { placeholder: '...', required: true, value: 'Diabetes' } },
 *   select: { role: { required: true, values: [{ label: 'Doctor', value: 'Doctor' }], value: 'Nurse' } },
 * }
 */

/**
 * Resolves a field's raw stored value against its `options` list, case-insensitively —
 * matching by value first, falling back to matching by label (in case the stored value is
 * actually label text rather than the raw value). Preserves the input's shape: an array in
 * (multi-select fields, e.g. product/country) yields an array out with each element resolved
 * independently; a string in yields a string out.
 *
 * @param {string|string[]} rawValue
 * @param {Array<string|{value?: string, label?: string}>} [options]
 * @returns {{ value: string|string[], label: string }}
 */
export function resolveOptionValue(rawValue, options) {
  if (!options?.length || rawValue == null || rawValue === '') {
    return { value: rawValue ?? '', label: '' };
  }

  const byValue = {};
  const valueToLabel = {};
  const labelToValue = {};
  (options || []).forEach((opt) => {
    const v = typeof opt === 'string' ? opt : opt.value || '';
    const l = typeof opt === 'string' ? opt : opt.label || opt.value || '';
    byValue[v.toLowerCase()] = v;
    valueToLabel[v.toLowerCase()] = l;
    labelToValue[l.toLowerCase()] = v;
  });

  const resolveOne = (raw) => {
    const lower = String(raw).toLowerCase();
    return byValue[lower] ?? labelToValue[lower] ?? raw;
  };

  if (Array.isArray(rawValue)) {
    const resolvedValues = rawValue.map(resolveOne);
    const label = resolvedValues.map((v) => valueToLabel[String(v).toLowerCase()] || v).join(', ');
    return { value: resolvedValues, label };
  }

  const resolvedValue = resolveOne(rawValue);
  const label = valueToLabel[String(resolvedValue).toLowerCase()] || '';
  return { value: resolvedValue, label };
}

export function applyDataContentToEditor(editor, dataContent) {
  if (!editor) return;

  if (
    !dataContent ||
    (Array.isArray(dataContent) && dataContent.length === 0) ||
    (typeof dataContent === 'object' && Object.keys(dataContent).length === 0)
  ) {
    // Clear the editor if no dataContent
    editor.commands.clearContent(true);
    return;
  }

  let normalized = [];

  if (Array.isArray(dataContent)) {
    normalized = dataContent.map((item) => ({
      key: item.key,
      kind: item.kind || 'input',
      placeholder: item.placeholder || item.label || '',
      required: !!item.required,
      multiple: item.multiple,
      value: item.value ?? item.defaultValue ?? '',
      options:
        Array.isArray(item.options) && item.kind === 'select'
          ? item.options.map((opt) => {
              if (typeof opt === 'string') {
                return { label: opt, value: opt };
              }

              if (opt && typeof opt === 'object') {
                return { label: opt.label || opt.value || '', value: opt.value || opt.label || '' };
              }

              return { label: '', value: '' };
            })
          : [],
    }));
  } else if (typeof dataContent === 'object') {
    for (const [kind, fields] of Object.entries(dataContent)) {
      for (const [key, field] of Object.entries(fields)) {
        normalized.push({
          key,
          kind,
          placeholder: field.placeholder || field.label || '',
          required: !!field.required,
          multiple: field.multiple,
          value: field.value ?? field.defaultValue ?? '',
          options:
            kind === 'select'
              ? (field.values || []).map((opt) => {
                  if (typeof opt === 'string') {
                    return { label: opt, value: opt };
                  }

                  if (opt && typeof opt === 'object') {
                    return { label: opt.label || opt.value || '', value: opt.value || opt.label || '' };
                  }

                  return { label: '', value: '' };
                })
              : [],
        });
      }
    }
  }

  if (!normalized.length) {
    editor.commands.clearContent(true);
    return;
  }

  const { state } = editor;
  const tr = state.tr;
  let changed = false;

  state.doc.descendants((node, pos) => {
    if (!node.attrs?.variable) return;

    const variable = node.attrs.variable;
    const field = normalized.find((f) => f.key === variable);
    if (!field) return;

    // Build option value/label maps for select fields (keyed by lowercase for case-insensitive lookup)
    const buildOptMaps = (options) => {
      const byValue = {}; // lowercase value → original-case value
      const valueToLabel = {}; // lowercase value → label
      const labelToValue = {}; // lowercase label → original-case value
      (options || []).forEach((opt) => {
        const v = typeof opt === 'string' ? opt : opt.value || '';
        const l = typeof opt === 'string' ? opt : opt.label || opt.value || '';
        byValue[v.toLowerCase()] = v;
        valueToLabel[v.toLowerCase()] = l;
        labelToValue[l.toLowerCase()] = v;
      });
      return { byValue, valueToLabel, labelToValue };
    };

    // Compute valueToSet: preserve original case; for select, resolve a label to its actual value
    let valueToSet = '';
    if (Array.isArray(field.value)) {
      valueToSet = field.value.map((v) => decodeHtmlEntitiesDeep(String(v))).join(',');
    } else if (typeof field.value === 'string') {
      valueToSet = decodeHtmlEntitiesDeep(field.value);
    }

    if (field.kind === 'select' && valueToSet && field.options?.length) {
      const { byValue, labelToValue } = buildOptMaps(field.options);
      const lower = valueToSet.toLowerCase();
      // Resolve: prefer matching by value, fall back to matching by label
      valueToSet = byValue[lower] ?? labelToValue[lower] ?? valueToSet;
    }

    const updatedAttrs = {
      ...node.attrs,
      variable,
      placeholder: field.placeholder || field.label || '',
      required: field.required || false,
      multiple: field.multiple || false,
      value: valueToSet,
    };

    if (field.kind === 'select') {
      updatedAttrs.options = field.options;

      // Resolve display label for pre-filled values (exact lookup first, then comma-split for multi-select)
      if (valueToSet && field.options?.length) {
        const { valueToLabel } = buildOptMaps(field.options);
        const directLabel = valueToLabel[valueToSet.toLowerCase()];
        updatedAttrs.label =
          directLabel ||
          valueToSet
            .split(',')
            .map((v) => valueToLabel[v.trim().toLowerCase()] || v.trim())
            .join(', ');
      }
    }

    // Only change if something actually differs
    const hasChanged = Object.keys(updatedAttrs).some((k) => updatedAttrs[k] !== node.attrs[k]);

    if (hasChanged) {
      changed = true;
      tr.setNodeMarkup(pos, undefined, updatedAttrs);
    }
  });

  if (changed) {
    editor.view.dispatch(tr);
  }
}

/**
 * Parses `data-variable`/`data-value`/`data-label`/`data-multiple` attributes out of the
 * editor's serialized HTML. This is the single place that reads variable values back out of
 * TipTap's HTML-escaped attribute strings, so it's also the single place that decodes them.
 * @param {string} html
 * @returns {{ key: string, value: string, label: string, multiple: boolean }[]}
 */
export const extractVariablesFromHTML = (html) => {
  if (!html) return [];

  const spanRegex = /<span\b[^>]*data-variable="[^"]*"[^>]*>/g;
  const variables = [];
  let spanMatch;

  while ((spanMatch = spanRegex.exec(html)) !== null) {
    const spanTag = spanMatch[0];

    const variableMatch = spanTag.match(/data-variable="([^"]*)"/);
    const valueMatch = spanTag.match(/data-value="([^"]*)"/);
    const labelMatch = spanTag.match(/data-label="([^"]*)"/);
    const multipleMatch = spanTag.match(/data-multiple="([^"]*)"/);

    if (!variableMatch) continue;

    variables.push({
      key: variableMatch[1],
      value: valueMatch?.[1] ? decodeHtmlEntitiesDeep(valueMatch[1]) : '',
      label: labelMatch?.[1] ? decodeHtmlEntitiesDeep(labelMatch[1]) : '',
      multiple: multipleMatch ? multipleMatch[1] === 'true' : false,
    });
  }

  return variables;
};

export const getRequiredFieldKeys = (dataContent) => {
  if (!dataContent) return [];

  if (Array.isArray(dataContent)) {
    // New format
    return dataContent
      .filter((f) => f && f.required)
      .map((f) => f.key)
      .filter(Boolean);
  } else if (typeof dataContent === 'object') {
    // Old format
    const keys = [];
    for (const group of Object.values(dataContent)) {
      if (!group) continue;
      for (const [key, field] of Object.entries(group || {})) {
        if (field && field.required) keys.push(key);
      }
    }
    return keys;
  }
  return [];
};

export const hasEmptyRequiredFields = (variables = [], requiredKeys = []) => {
  if (!variables.length || !requiredKeys.length) return false;

  return requiredKeys.some((key) => {
    const field = variables.find((v) => v.key === key);
    if (!field) return false; // If the key doesn’t exist, ignore it
    return !field.value || field.value.trim() === '';
  });
};

export const disableInteractiveElements = (editor) => {
  const root = editor.view.dom;

  // Disable variableSelect custom dropdowns
  root.querySelectorAll('span.variable-select').forEach((el) => {
    // Disable main display (so it can’t open)
    const display = el.querySelector('.variable-select-control');
    if (display) {
      display.setAttribute('tabindex', '-1');
    }

    // Hide or disable dropdown options
    const dropdown = el.querySelector('.variable-select-dropdown');
    if (dropdown) {
      dropdown.style.display = 'none'; // ensure hidden when disabled
    }

    // Remove chevron if it exists
    const chevron = el.querySelector('.chevron');
    if (chevron) {
      chevron.remove();
    }
  });

  // Disable variableInput spans and handle multi-line underlines
  root.querySelectorAll('span.variable-input').forEach((el) => {
    const editable = el.querySelector('[contenteditable="true"]');
    if (!editable) return;

    editable.contentEditable = 'false';
    editable.setAttribute('tabindex', '-1');
  });
};
